import React, { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown, FileText, FileSpreadsheet, X, Bookmark } from 'lucide-react';
import { downloadCSV, downloadExcel, formatQuoteForExport } from '../utils/exportUtils';

interface ExportButtonProps {
  selectedQuotes: any[];
  allQuotes: any[];
  onClearSelection: () => void;
  onAddToQuotewall: (quoteIds: string[]) => Promise<void>;
}

export default function ExportButton({ selectedQuotes, allQuotes, onClearSelection, onAddToQuotewall }: ExportButtonProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAddingToQuotewall, setIsAddingToQuotewall] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleExport = (format: 'csv' | 'excel', data: any[]) => {
    if (data.length === 0) return;

    const formattedData = data.map(formatQuoteForExport);
    const filename = `quotes-export-${new Date().toISOString().split('T')[0]}`;

    if (format === 'csv') {
      downloadCSV(formattedData, filename);
    } else {
      downloadExcel(formattedData, filename);
    }

    setShowDropdown(false);
    if (data === selectedQuotes) {
      onClearSelection();
    }
  };

  const handleAddToQuotewall = async () => {
    if (selectedQuotes.length === 0) return;

    setIsAddingToQuotewall(true);
    try {
      await onAddToQuotewall(selectedQuotes.map(quote => quote.id));
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      onClearSelection();
    } catch (error) {
      console.error('Failed to add to quotewall:', error);
    } finally {
      setIsAddingToQuotewall(false);
      setShowDropdown(false);
    }
  };

  const hasSelection = selectedQuotes.length > 0;

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {showSuccess && (
        <div className="p-4 bg-green-50 text-green-700 rounded-lg flex items-center">
          <span>Successfully added to Quotewall!</span>
          <button
            onClick={() => setShowSuccess(false)}
            className="ml-auto text-green-700 hover:text-green-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Selected Quotes Count */}
      {hasSelection && (
        <div className="p-4 bg-blue-50 rounded-lg flex items-center">
          <span className="text-blue-700">
            {selectedQuotes.length} quote{selectedQuotes.length === 1 ? '' : 's'} selected
          </span>
          <button
            onClick={onClearSelection}
            className="ml-auto text-blue-700 hover:text-blue-900 flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Clear selection
          </button>
        </div>
      )}

      {/* Unified Download Button with Dropdown */}
      <div className="relative">
        <div className="flex items-center gap-4">
          <span className="text-gray-700">
            {allQuotes.length} total quote{allQuotes.length === 1 ? '' : 's'}
          </span>
          
          <div className="relative ml-auto">
            <button
              ref={buttonRef}
              onClick={() => setShowDropdown(!showDropdown)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              aria-expanded={showDropdown}
              aria-haspopup="true"
            >
              <Download className="h-4 w-4" />
              {hasSelection ? 'Export Selected' : 'Download'}
              <ChevronDown className={`h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-10"
                role="menu"
                aria-orientation="vertical"
                onKeyDown={handleKeyDown}
              >
                {hasSelection && (
                  <div className="py-1" role="none">
                    <div className="px-4 py-2 text-sm text-gray-500">Selected Quotes</div>
                    <button
                      onClick={handleAddToQuotewall}
                      disabled={isAddingToQuotewall}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50"
                      role="menuitem"
                    >
                      <Bookmark className="h-4 w-4" />
                      {isAddingToQuotewall ? 'Adding to Quotewall...' : 'Add to Quotewall'}
                    </button>
                    <button
                      onClick={() => handleExport('csv', selectedQuotes)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      role="menuitem"
                    >
                      <FileText className="h-4 w-4" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('excel', selectedQuotes)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      role="menuitem"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export as Excel
                    </button>
                  </div>
                )}

                {!hasSelection && (
                  <div className="py-1" role="none">
                    <div className="px-4 py-2 text-sm text-gray-500">All Quotes</div>
                    <button
                      onClick={() => handleExport('csv', allQuotes)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      role="menuitem"
                    >
                      <FileText className="h-4 w-4" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('excel', allQuotes)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      role="menuitem"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export as Excel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}